import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  sidebarClasses,
  menuClasses,
} from 'react-pro-sidebar'
import { Button } from '@material-tailwind/react'
import Link from 'next/link'
import Image from 'next/image'
import { Plus, BookOpen, Lock, ChevronDown, ChevronUp, Box } from 'lucide-react'
import { RecipeIcon, DiscoverIcon, DashboardIcon, PeopleIcon, ShoppingIcon, MealPlannerIcon } from './icons'
import { useOptionalShoppingLists } from './shopping-lists/ShoppingListsProvider'
import { canUseShopping } from '@/lib/shoppingLists/access'
import { canUseMealPlanner } from '@/lib/mealPlanner/access'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import { isUserAvailable } from 'helpers/authUser'
import React from 'react'
import { VerticalUpgradeBanner } from './ui/upgrade-banner'
import ProfileCompletionWidget from './profile/ProfileCompletionWidget'
import ProducerProfileCompletionWidget from './profile/ProducerProfileCompletionWidget'
import RecipeImportModal from './recipe/RecipeImportModal'
import { AccountManagementModal } from './account-management-modal'
import { useListingDetailQuery, useCancelSubscriptionMutation } from 'graphql/generated/graphql'
import { getFollowerCount } from '@/lib/followerCount'
import { UpgradePaymentModal } from './modals/upgrade-payment-modal'
import { UpdatePaymentModal } from './modals/update-payment-modal'
import { UpgradeSuccessModal } from './modals/upgrade-success-modal'
import { FormDataProvider } from 'hook/useFormData'
import { toast } from 'react-toastify'
import { useAuth } from '@/lib/authContext'
import { ConfirmDialog } from './confirm-dialog'
import { Elements } from '@stripe/react-stripe-js'
import { getStripe } from '@/lib/stripe'


type SidebarCulineerProps = {
  setToggled: (toggled: boolean) => void
  toggled: boolean
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  userDetail
}

type MenuItemConfig = {
  path: string
  label: string
  icon: React.ReactNode
  badge?: number | string | React.ReactNode
  subItems?: MenuItemConfig[]
  comingSoon?: boolean
  requiresAuth?: boolean
  isActive?: (pathname: string, search: string) => boolean
}

const isQuery = (search: string, key: string, value: string) =>
  new URLSearchParams(search).get(key) === value

const isDiscoverNavActive = (pathname: string, search: string) => {
  if (pathname === '/discover' || pathname.startsWith('/discover/')) return true
  if (pathname.startsWith('/ingredients')) return true
  if (pathname === '/display') return true
  if (pathname === '/recipes' && isQuery(search, 'query', 'all')) return true
  return false
}

const isCookingNavActive = (pathname: string, search: string) => {
  if (!pathname.startsWith('/recipes')) return false
  if (pathname === '/recipes' && isQuery(search, 'query', 'all')) return false
  return true
}

const getAuthenticatedMenuItems = (
  counts?: {
    followers?: number | null
    following?: number | null
  },
  options?: {
    isProducer?: boolean
    productCount?: number
    shoppingItemCount?: number
    showShopping?: boolean
    showMealPlanner?: boolean
  }
): MenuItemConfig[] => {
  const items: MenuItemConfig[] = [
    {
      path: '/discover',
      label: 'Discover',
      icon: <DiscoverIcon width={22} height={22} />,
      isActive: isDiscoverNavActive,
      subItems: [
        {
          path: '/recipes?query=all',
          label: 'Recipes',
          icon: null,
          isActive: (pathname, search) =>
            pathname === '/recipes' && isQuery(search, 'query', 'all'),
        },
        {
          path: '/display?type=producers',
          label: 'Producers',
          icon: null,
          isActive: (pathname, search) =>
            pathname === '/display' && isQuery(search, 'type', 'producers'),
        },
        {
          path: '/ingredients/seasonal',
          label: 'Ingredients',
          icon: null,
          isActive: (pathname) => pathname.startsWith('/ingredients'),
        },
        {
          path: '/display?type=markets',
          label: 'Markets',
          icon: null,
          isActive: (pathname, search) =>
            pathname === '/display' && isQuery(search, 'type', 'markets'),
        },
      ],
    },
    {
      path: '/recipes',
      label: 'Cooking',
      icon: <RecipeIcon width={22} height={22} />,
      isActive: isCookingNavActive,
      subItems: [
        {
          path: '/recipes',
          label: 'My Recipes',
          icon: null,
          isActive: (pathname, search) =>
            pathname === '/recipes' && !isQuery(search, 'query', 'all'),
        },
        {
          path: '/recipes/bookmarks',
          label: 'Bookmarks',
          icon: null,
        },
      ],
    },
    ...(options?.showMealPlanner === false
      ? []
      : [
          {
            path: '/meal-planner',
            label: 'Meal Planner',
            icon: <MealPlannerIcon width={22} height={22} />,
            isActive: (pathname: string) => pathname.startsWith('/meal-planner'),
          },
        ]),
    ...(options?.showShopping === false
      ? []
      : [
          {
            path: '/shopping-lists',
            label: 'Shopping',
            icon: <ShoppingIcon width={22} height={22} />,
            badge: options?.shoppingItemCount || undefined,
            isActive: (pathname: string) =>
              pathname === '/shopping-lists' || pathname.startsWith('/shopping-lists/'),
          },
        ]),
    {
      path: '/community',
      label: 'Community',
      icon: <PeopleIcon width={22} height={22} />,
      isActive: (pathname) =>
        pathname === '/community' ||
        pathname.startsWith('/community/') ||
        pathname === '/connect' ||
        pathname.startsWith('/connect/') ||
        pathname.startsWith('/post/') ||
        pathname === '/create-post',
      subItems: [
        {
          path: '/community',
          label: 'Feed',
          icon: null,
          isActive: (pathname) =>
            pathname === '/community' || pathname.startsWith('/post/'),
        },
        {
          path: '/connect',
          label: 'Connect',
          icon: null,
          isActive: (pathname, search) =>
            pathname === '/connect' && (!search || (!search.includes('tab=followers') && !search.includes('tab=following'))),
        },
        {
          path: '/connect?tab=followers',
          label: 'Followers',
          icon: null,
          badge: counts?.followers,
          isActive: (pathname, search) =>
            pathname === '/connect' && search?.includes('tab=followers'),
        },
        {
          path: '/connect?tab=following',
          label: 'Following',
          icon: null,
          badge: counts?.following,
          isActive: (pathname, search) =>
            pathname === '/connect' && search?.includes('tab=following'),
        },
      ],
    },
    {
      path: '/blog',
      label: 'Learn',
      icon: <BookOpen size={22} />,
    },
  ]

  if (options?.isProducer) {
    return [
      {
        path: '/dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon width={24} height={24} />,
        isActive: (pathname) => pathname === '/dashboard',
      },
      ...items.slice(0, 1),
      {
        path: '/dashboard/products',
        label: 'Products',
        icon: <Box size={22} strokeWidth={1.5} />,
        badge: options.productCount,
        isActive: (pathname) => pathname.startsWith('/dashboard/products'),
      },
      ...items.slice(1),
    ]
  }

  return items
}

const NON_AUTHENTICATED_MENU_ITEMS: MenuItemConfig[] = [
  {
    path: '/discover',
    label: 'Discover',
    icon: <DiscoverIcon width={22} height={22} />,
    requiresAuth: true,
  },
  {
    path: '/recipes',
    label: 'Cooking',
    icon: <RecipeIcon width={22} height={22} />,
    requiresAuth: true,
  },
  {
    path: '/community',
    label: 'Community',
    icon: <PeopleIcon width={22} height={22} />,
    requiresAuth: true,
  },
  {
    path: '/blog',
    label: 'Learn',
    icon: <BookOpen size={22} />,
  },
]

const NAV_B600 = '#202020'

const getSubItemStyles = (isActive: boolean) => ({
  backgroundColor: isActive ? '#E5F3D4' : 'transparent',
  color: NAV_B600,
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: isActive ? 600 : 500,
  fontSize: '14px',
  lineHeight: '150%',
  letterSpacing: '0%',
  textAlign: 'left' as const,
  paddingLeft: '65px',
  paddingRight: '10px',
  opacity: isActive ? 1 : 0.8,
  '&:hover': {
    backgroundColor: isActive ? '#E5F3D4' : '#F3FAEB',
  },
})

const getTopLevelStyles = (isActive: boolean) => ({
  backgroundColor: isActive ? '#E5F3D4' : 'transparent',
  color: NAV_B600,
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: isActive ? 600 : 500,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0%',
  '&:hover': {
    backgroundColor: isActive ? '#E5F3D4' : '#F3FAEB',
  },
})

const getSubMenuPanelStyles = (open: boolean, collapsed: boolean) =>
  collapsed || !open
    ? {
        backgroundColor: 'transparent',
        padding: 0,
      }
    : {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '4px 0',
      }

export const SidebarCulineer = ({
  setToggled,
  toggled,
  collapsed,
  setCollapsed,
  userDetail,
}: SidebarCulineerProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ''
  const { showAuthModal } = useAuth()
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [recipeSubmenuOpen, setRecipeSubmenuOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const addMenuRef = useRef<HTMLDivElement>(null)
  const [accountModalOpen, setAccountModalOpen] = useState(false)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [isTaxExempt, setIsTaxExempt] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelSubscription, { loading: cancelLoading }] = useCancelSubscriptionMutation({ fetchPolicy: 'no-cache' })
  const { user } = useAppSelector((state: RootState) => state.culineerAuth)
  const userAvailable = isUserAvailable(user)
  const shoppingLists = useOptionalShoppingLists()

  const getActiveMenuKey = useCallback(() => {
    if (pathname.startsWith('/dashboard/products')) return '/dashboard/products'
    if (pathname === '/dashboard') return '/dashboard'
    if (isDiscoverNavActive(pathname, search)) return '/discover'
    if (isCookingNavActive(pathname, search)) return '/recipes'
    if (pathname.startsWith('/meal-planner')) return '/meal-planner'
    if (pathname === '/shopping-lists' || pathname.startsWith('/shopping-lists/')) {
      return '/shopping-lists'
    }
    if (
      pathname === '/community' ||
      pathname.startsWith('/community/') ||
      pathname === '/connect' ||
      pathname.startsWith('/connect/') ||
      pathname.startsWith('/post/') ||
      pathname === '/create-post'
    ) {
      return '/community'
    }
    return null
  }, [pathname, search])

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(() => getActiveMenuKey())

  useEffect(() => {
    const activeKey = getActiveMenuKey()
    if (activeKey) {
      setOpenSubmenu(activeKey)
    }
  }, [getActiveMenuKey])

  const { data: listing } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: {
      listingId: userDetail?.userProfile.default_listing_id,
    },
    skip: !userDetail?.userProfile?.default_listing_id,
  })

  const handleImportSuccess = (scrapedData: any) => {
    sessionStorage.setItem('importedRecipeData', JSON.stringify(scrapedData))
    router.push('/recipes/add?imported=true')
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        addMenuRef.current &&
        !addMenuRef.current.contains(event.target as Node)
      ) {
        setAddMenuOpen(false)
        setRecipeSubmenuOpen(false)
      }
    }
    if (addMenuOpen) {
      document.addEventListener('click', handleClickOutside)
    }
    return () => document.removeEventListener('click', handleClickOutside)
  }, [addMenuOpen])

  const renderMenuItems = (items: MenuItemConfig[]) => (
    <>
      {items.map((item) => {
        const isActive = item.isActive
          ? item.isActive(pathname, search)
          : pathname.startsWith(item.path)
        const iconWithStyle =
          item.icon && React.cloneElement(item.icon as React.ReactElement)

        if (item.requiresAuth) {
          return (
            <MenuItem
              key={item.path}
              icon={iconWithStyle}
              onClick={() => showAuthModal(item.path)}
              component={<span />}
              rootStyles={{
                [`.${menuClasses.button}`]: {
                  ...getTopLevelStyles(false),
                  opacity: 0.45,
                  marginLeft: 5,
                  marginRight: 5,
                },
                [`.${menuClasses.label}`]: {
                  color: NAV_B600,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '150%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              }}
            >
              {item.label}
              <Lock size={12} style={{ color: '#9ca3af', marginLeft: 8, flexShrink: 0 }} />
            </MenuItem>
          )
        }

        if (item.comingSoon) {
          return (
            <MenuItem
              key={item.path}
              icon={iconWithStyle}
              component={<span />}
              rootStyles={{
                [`.${menuClasses.button}`]: {
                  ...getTopLevelStyles(false),
                  opacity: 0.4,
                  cursor: 'default',
                  pointerEvents: 'none',
                  marginLeft: 5,
                  marginRight: 5,
                },
                [`.${menuClasses.label}`]: {
                  color: NAV_B600,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '150%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              }}
            >
              {item.label}
              <span style={{ fontSize: 10, background: '#e5e7eb', borderRadius: 999, padding: '2px 6px', marginLeft: 8, color: '#9ca3af' }}>
                Soon
              </span>
            </MenuItem>
          )
        }

        return item.subItems ? (
          <SubMenu
            key={item.path}
            label={item.label}
            icon={iconWithStyle}
            active={isActive}
            open={openSubmenu === item.path}
            onOpenChange={(open) => {
              setOpenSubmenu(open ? item.path : null)
            }}
            onClick={(e) => {
              // Ignore clicks on child links so Recipes/etc. don't bounce to Discover.
              const target = e.target as HTMLElement
              if (target.closest(`.${menuClasses.subMenuContent}`)) return

              // Parent is the section landing page, not a duplicate child item.
              const childSharesParentPath = item.subItems?.some(
                (subItem) => subItem.path.split('?')[0] === item.path
              )
              if (!childSharesParentPath && item.path) {
                router.push(item.path)
              }
            }}
            rootStyles={{
              [`& > .${menuClasses.button}`]: getTopLevelStyles(isActive),
              [`.${menuClasses.subMenuContent}`]: getSubMenuPanelStyles(
                openSubmenu === item.path,
                collapsed
              ),
                  [`& > .${menuClasses.button} .${menuClasses.label}`]: {
                color: NAV_B600,
                fontFamily: 'Inter, sans-serif',
                fontWeight: isActive ? 600 : 500,
                fontSize: '16px',
                lineHeight: '150%',
              },
            }}
          >
            {item.subItems.map((subItem) => {
              const isSubItemActive = subItem.isActive
                ? subItem.isActive(pathname, search)
                : pathname === subItem.path.split('?')[0] &&
                (subItem.path.includes('?')
                  ? search === subItem.path.split('?')[1]
                  : true)
              return (
                <MenuItem
                  key={subItem.path + subItem.label}
                  component={<Link href={subItem.path} />}
                  active={isSubItemActive}
                  rootStyles={{
                    [`& > .${menuClasses.button}`]: getSubItemStyles(isSubItemActive),
                    [`.${menuClasses.label}`]: {
                      color: NAV_B600,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: isSubItemActive ? 600 : 500,
                      fontSize: '14px',
                      lineHeight: '150%',
                      textAlign: 'left',
                      opacity: isSubItemActive ? 1 : 0.85,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    },
                  }}
                >
                  <span>{subItem.label}</span>
                  {subItem.badge !== undefined && subItem.badge !== null && (
                    <span
                      className={`text-xs ${isSubItemActive
                        ? 'font-semibold text-[#202020]'
                        : 'font-medium text-[#757575]'
                        } ml-auto`}
                    >
                      {subItem.badge}
                    </span>
                  )}
                </MenuItem>
              )
            })}
          </SubMenu>
        ) : (
          <MenuItem
            key={item.path}
            icon={iconWithStyle}
            component={<Link href={item.path} />}
            active={isActive}
            rootStyles={{
              [`& > .${menuClasses.button}`]: getTopLevelStyles(isActive),
              [`.${menuClasses.label}`]: {
                color: NAV_B600,
                fontFamily: 'Inter, sans-serif',
                fontWeight: isActive ? 600 : 500,
                fontSize: '16px',
                lineHeight: '150%',
              },
            }}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span>{item.label}</span>
              {item.badge !== undefined && item.badge !== null && (
                <span className="text-sm font-normal text-[#6D6D6D] opacity-80">
                  {item.badge}
                </span>
              )}
            </span>
          </MenuItem>
        )
      })}
    </>
  )

  const renderTopActions = () => {
    return (
      <div ref={addMenuRef} className="relative pt-4 pb-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setAddMenuOpen((open) => {
              if (open) setRecipeSubmenuOpen(false)
              return !open
            })
          }}
          className="flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-[#85C349] px-4 text-sm font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-[#75B339]"
        >
          <Plus className="h-4 w-4 flex-shrink-0 stroke-[2.5]" />
          <span>Add New</span>
        </button>

        {addMenuOpen && (
          <div
            className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setAddMenuOpen(false)
                setRecipeSubmenuOpen(false)
                router.push('/create-post')
              }}
              className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-100"
            >
              <span className="text-lg">📝</span>
              Add Feed Post
            </button>

            {canUseShopping(user) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setAddMenuOpen(false)
                  setRecipeSubmenuOpen(false)
                  router.push('/shopping-lists?new=1')
                }}
                className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-100"
              >
                <span className="text-lg">🛒</span>
                Add Shopping List
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setRecipeSubmenuOpen((open) => !open)
              }}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">🍽️</span>
                Add a New Recipe
              </div>
              <span className="text-gray-400">
                {recipeSubmenuOpen ? '▲' : '▼'}
              </span>
            </button>

            {recipeSubmenuOpen && (
              <div className="border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setRecipeSubmenuOpen(false)
                    setAddMenuOpen(false)
                    router.push('/recipes/add')
                  }}
                  className="flex w-full items-center gap-2 border-b border-gray-100 px-4 py-3 pl-10 text-left text-sm hover:bg-gray-100"
                >
                  <span>✏️</span>
                  Type it in
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setRecipeSubmenuOpen(false)
                    setAddMenuOpen(false)
                    setImportModalOpen(true)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-3 pl-10 text-left text-sm hover:bg-gray-100"
                >
                  <span>🔗</span>
                  Paste a link
                </button>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 pb-1 text-sm font-semibold leading-[150%] text-[#202020] opacity-60">
          Menu
        </div>
      </div>
    )
  }

  const renderAuthButtons = () => (
    <div className="px-4 py-2">
      <div className="mb-4 text-center">
        <p className="text-sm text-gray-600">
          Find farms, markets, and producers near you.
        </p>
      </div>

      <Link href="/auth/join-culineer">
        <Button
          className="mb-3 flex w-full transform items-center justify-center gap-2 rounded-full bg-[#85C349] px-6 py-3 text-white shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:bg-[#75B339] hover:shadow-lg"
        >
          <span>Join free</span>
        </Button>
      </Link>

      <Link href="/auth/login">
        <Button
          variant="outlined"
          className="flex w-full transform items-center justify-center gap-2 rounded-full border-[#85C349] bg-white px-6 py-3 text-[#85C349] shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:bg-gray-50 hover:shadow-lg"
        >
          <span>Sign In</span>
        </Button>
      </Link>
    </div>
  )

  const handleUpgrade = () => {
    setAccountModalOpen(false)
    setPaymentModalOpen(true)
  }

  const handlePaymentSuccess = (taxExempt: boolean) => {
    setIsTaxExempt(taxExempt)
    setPaymentModalOpen(false)
    setSuccessModalOpen(true)
  }

  const handleManageSubscription = () => {
    setAccountModalOpen(false)
    setUpdatePaymentModalOpen(true)
  }

  const handleUpdatePaymentSuccess = () => {
    setUpdatePaymentModalOpen(false)
  }

  const handleCancelSubscription = () => {
    setAccountModalOpen(false)
    setShowCancelConfirm(true)
  }

  const confirmCancelSubscription = async () => {
    try {
      const result = await cancelSubscription()

      if (result.data?.cancelSubscription?.success) {
        toast.success(result.data.cancelSubscription.message || 'Subscription canceled successfully')
        setShowCancelConfirm(false)

        setTimeout(() => {
          router.refresh()
        }, 2000)
      } else {
        toast.error(result.data?.cancelSubscription?.message || 'Failed to cancel subscription')
      }
    } catch (error) {
      console.error('Cancel subscription error:', error)
      toast.error('An error occurred while canceling your subscription')
    }
  }

  const getUserData = () => {
    const defaultListing = user?.default_listing || listing?.listingDetail
    return {
      firstName: defaultListing?.firstName || defaultListing?.first_name || user?.first_name || '',
      lastName: defaultListing?.lastName || defaultListing?.last_name || user?.last_name || '',
      email: defaultListing?.email || user?.email_address || '',
      phone: defaultListing?.phone || user?.phone_number || '',
      address: defaultListing?.address || '',
      city: defaultListing?.city || user?.city || '',
      state: defaultListing?.state || user?.state || '',
      zip: defaultListing?.zipcode || user?.zipcode || '',
    }
  }

  const getUserSlug = () => {
    return listing?.listingDetail?.slug || user?.default_listing?.slug || user?.slug || ''
  }

  const renderVerificationBanner = () => {
    const membershipType = listing?.listingDetail?.membership_type ||
      user?.default_listing?.membership_type ||
      userDetail?.userProfile?.membership_type

    const isEnhanced = listing?.listingDetail?.enhanced || user?.default_listing?.enhanced || false

    const shouldShowBanner = membershipType?.toLowerCase() === 'verified business' && !isEnhanced

    if (!shouldShowBanner) return null

    return (
      <div className="px-4 py-2">
        <VerticalUpgradeBanner onUpgradeClick={() => setAccountModalOpen(true)} />
      </div>
    )
  }

  return (
    <div className={`flex h-full ${toggled ? '' : 'hidden lg:block'}`} style={{ border: 'none' }}>
      <Sidebar
        className="h-full public-sidebar"
        breakPoint="xs"
        transitionDuration={1000}
        collapsed={collapsed}
        onBackdropClick={() => setToggled(false)}
        toggled={toggled}
        rootStyles={{
          border: 'none',
          [`.${sidebarClasses.container}`]: {
            backgroundColor: '#F7F7F7',
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
            border: 'none',
            borderRight: '1px solid #E6E6E6',
            boxSizing: 'border-box',
          },
        }}
      >
        <div className="flex w-full items-center justify-center pt-6 sm:hidden xs:flex">
          <Image
            alt="Culineer Logo"
            src="/assets/logo.svg"
            width={50}
            height={60}
            className="pointer-events-none h-auto max-h-[60px] w-[45%]"
          />
        </div>

        <div className="flex flex-col h-full">
          <div className="overflow-y-auto py-2 flex-1">
            {userAvailable ? (
              <>
                {renderTopActions()}
                <Menu
                  renderExpandIcon={({ open }) =>
                    open ? (
                      <ChevronUp size={18} className="text-[#202020] opacity-70 mr-1" />
                    ) : (
                      <ChevronDown size={18} className="text-[#202020] opacity-70 mr-1" />
                    )
                  }
                  menuItemStyles={{
                    button: ({ level, active }) =>
                      level === 0 ? getTopLevelStyles(active) : getSubItemStyles(active),
                    label: ({ level }) =>
                      level === 0
                        ? {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '150%',
                            color: NAV_B600,
                          }
                        : {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: NAV_B600,
                            textAlign: 'left' as const,
                          },
                    subMenuContent: ({ open }) => getSubMenuPanelStyles(open, collapsed),
                  }}
                  onClick={(e) => {
                    // Only close sidebar on mobile when clicking menu items (not submenus)
                    const target = e.target as HTMLElement
                    if (toggled && target.closest(`.${menuClasses.button}`) && !target.closest(`.${menuClasses.subMenuContent}`)) {
                      setToggled(false)
                    }
                  }}
                >
                  {renderMenuItems(
                    getAuthenticatedMenuItems(
                      {
                        followers: getFollowerCount(userDetail?.userProfile),
                        following: userDetail?.userProfile?.followings?.length,
                      },
                      {
                        isProducer:
                          user?.user_type?.toLowerCase() === 'producer' ||
                          userDetail?.userProfile?.user_type?.toLowerCase() === 'producer',
                        productCount: listing?.listingDetail?.ingredients?.length,
                        shoppingItemCount: shoppingLists?.totalItemCount,
                        showShopping: canUseShopping(user),
                        showMealPlanner: canUseMealPlanner(user),
                      }
                    )
                  )}
                </Menu>
              </>
            ) : (
              <>
                <div className="px-5 pt-3 pb-1 text-xs font-semibold text-[#757575]">
                  Menu
                </div>
                <Menu
                  renderExpandIcon={({ open }) =>
                    open ? (
                      <ChevronUp size={18} className="text-[#202020] opacity-70 mr-1" />
                    ) : (
                      <ChevronDown size={18} className="text-[#202020] opacity-70 mr-1" />
                    )
                  }
                  menuItemStyles={{
                    button: ({ level, active }) =>
                      level === 0 ? getTopLevelStyles(active) : getSubItemStyles(active),
                    label: ({ level }) =>
                      level === 0
                        ? {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '150%',
                            color: NAV_B600,
                          }
                        : {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: NAV_B600,
                            textAlign: 'left' as const,
                          },
                    subMenuContent: ({ open }) => getSubMenuPanelStyles(open, collapsed),
                  }}
                  onClick={(e) => {
                    // Only close sidebar on mobile when clicking menu items (not submenus)
                    const target = e.target as HTMLElement
                    if (toggled && target.closest(`.${menuClasses.button}`) && !target.closest(`.${menuClasses.subMenuContent}`)) {
                      setToggled(false)
                    }
                  }}
                >
                  {renderMenuItems(NON_AUTHENTICATED_MENU_ITEMS)}
                </Menu>
                {renderAuthButtons()}
              </>
            )}
          </div>

          {userAvailable && <ProfileCompletionWidget />}

          {userAvailable && ['producer', 'market'].includes(user?.user_type?.toLowerCase()) && (
            <ProducerProfileCompletionWidget listing={listing?.listingDetail} />
          )}

          {userAvailable && renderVerificationBanner()}
        </div>
      </Sidebar>

      {/* Recipe Import Modal */}
      <RecipeImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />

      {/* Account Management Modal */}
      {(userDetail?.userProfile?.user_type?.toLowerCase() === 'producer' ||
        userDetail?.userProfile?.user_type?.toLowerCase() === 'market') && (
          <AccountManagementModal
            open={accountModalOpen}
            onClose={() => setAccountModalOpen(false)}
            membershipType={listing?.listingDetail?.membership_type}
            enhanced={listing?.listingDetail?.enhanced}
            onUpgrade={handleUpgrade}
            onManageSubscription={handleManageSubscription}
            onCancelSubscription={handleCancelSubscription}
          />
        )}

      {/* Payment Modal */}
      {paymentModalOpen && (
        <FormDataProvider>
          <UpgradePaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            onSuccess={handlePaymentSuccess}
            userData={getUserData()}
          />
        </FormDataProvider>
      )}

      {/* Update Payment Modal */}
      {updatePaymentModalOpen && (
        <Elements stripe={getStripe()}>
          <UpdatePaymentModal
            open={updatePaymentModalOpen}
            onClose={() => setUpdatePaymentModalOpen(false)}
            onSuccess={handleUpdatePaymentSuccess}
            userData={getUserData()}
          />
        </Elements>
      )}

      {/* Success Modal */}
      {successModalOpen && (
        <UpgradeSuccessModal
          open={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          userSlug={getUserSlug()}
          isTaxExempt={isTaxExempt}
          listingSlug={userDetail?.userProfile?.default_listing?.slug}
          userType={userDetail?.userProfile?.user_type?.toLowerCase()}
        />
      )}

      {/* Cancel Subscription Confirmation */}
      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title="Cancel Subscription"
        description="Your subscription will be canceled at the end of your current billing period. You'll downgrade to Verified Business but retain Professional access until then. Are you sure?"
        confirmText={cancelLoading ? "Canceling..." : "Yes, Cancel Subscription"}
        cancelText="Keep Subscription"
        onConfirm={confirmCancelSubscription}
        variant="destructive"

      />
    </div>
  )
}
